import {
  Overlay,
  useClickOutside,
  colors,
  fontSizing,
  fontWeights,
  icons,
  layers,
  sizing,
} from '@chillibean/component-library';
import { Icon } from '@iconify/react';
import PropTypes from 'prop-types';
import { useState, useRef } from 'react';
import styled from 'styled-components';

export function FilterPicker({ currentOption, onClickOption, options }) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  useClickOutside({ active: isOpen, additionalRefs: [buttonRef], callback: () => setIsOpen(false), ref: listRef });

  const toggleIsOpen = () => {
    setIsOpen(!isOpen);
  };

  const handleClickedOption = async (option) => {
    onClickOption(option);
    setIsOpen(false);
  };

  if (options.length === 0) return null;

  return (
    <Container>
      <TestContainer>
        <ButtonContainer ref={buttonRef} onClick={toggleIsOpen} role="button">
          <SelectedWrapper>
            <Selected>{currentOption?.name}</Selected>
            <Icon height={16} icon={icons.baselineChevronDown} width={16} />
          </SelectedWrapper>
        </ButtonContainer>
      </TestContainer>
      {isOpen && (
        <>
          <Overlay translucent={false} />
          <ListWrapper ref={listRef}>
            <ul>
              {options.map((option) => (
                <DropdownListItem key={option.id} onClick={() => handleClickedOption(option)}>
                  {option.id === currentOption.id ? (
                    <Icon height={20} icon={icons.outlineCheck} width={20} />
                  ) : (
                    <FakeCheck />
                  )}
                  {option.name}
                </DropdownListItem>
              ))}
            </ul>
          </ListWrapper>
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  position: relative;
`;

const TestContainer = styled.div`
  margin: -8px;
`;

const ButtonContainer = styled.div`
  cursor: pointer;
  min-width: 90px;
  border-radius: ${sizing.xsmall};
  padding: 8px;

  &:hover {
    background-color: ${colors.black.light};
    transition: background-color 0.3s ease-in-out;
  }
`;

const SelectedWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Selected = styled.div`
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizing.medium};
  max-width: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ListWrapper = styled.div`
  width: 100%;
  min-width: 250px;
  max-height: 250px;
  position: absolute;
  box-sizing: border-box;
  margin-top: ${sizing.xsmall};
  background-color: ${colors.white.primary};
  border: 1px solid ${colors.black.medium};
  border-radius: ${sizing.xsmall};
  overflow-y: auto;
  z-index: ${layers.dropdown};
`;

const DropdownListItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${sizing.lsmall};
  padding: ${sizing.medium};
  cursor: pointer;

  &:hover {
    background-color: ${colors.blue.light};
  }
`;

const FakeCheck = styled.div`
  height: 20px;
  width: 20px;
`;

FilterPicker.propTypes = {
  currentOption: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }).isRequired,
  onClickOption: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.shape })).isRequired,
};

export default FilterPicker;
